/**
 * Wrapper de proteção de rotas — TH-53154.
 *
 * Renderiza children apenas quando o usuário está autenticado.
 * - Enquanto `useAuth().isLoading`: spinner discreto (evita flash do
 *   redirect antes da resposta de /auth/me).
 * - Não autenticado: navigate para /login (efeito colateral).
 * - super_admin sem tenant selecionado: navigate para /select-tenant
 *   (TH-54656). Bloqueia o resto do admin enquanto não houver seleção,
 *   espelhando o `PostLoginRouter` do mobile.
 */

import { useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { useAuth } from "@/hooks/useAuth";
import { useTenantSelectionStore } from "@/stores/tenantSelectionStore";

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const selection = useTenantSelectionStore((s) => s.selection);

  const needsTenantSelection =
    isAuthenticated &&
    user?.role === "super_admin" &&
    !selection &&
    location.pathname !== "/select-tenant";

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/login", replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (needsTenantSelection) {
      navigate({ to: "/select-tenant", replace: true });
    }
  }, [needsTenantSelection, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </span>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Render-null enquanto o navigate não dispara o unmount.
    return null;
  }

  if (needsTenantSelection) {
    return null;
  }

  return <>{children}</>;
}
